<template>
  <b-modal
    ref="modal"
    v-model="isShown"
    :title="title"
    modal-class="spso-modal"
    hide-header-close
    hide-footer
    centered
    size="lg"
    @shown="getItem"
    @hidden="hideModal"
  >
    <v-form
      ref="form"
      class="mt-3"
      @submit.prevent="submit"
    >
      <b-row class="v-application">
        <b-col
          cols="12"
          class="pb-0"
        >
          <div class="input-block input-block_white">
            <label class="input-block__label">
              {{ $t('common.part_number') }}
            </label>
            <v-autocomplete
              v-model="item.detailNumber"
              :loading="loadingParts"
              :items="parts"
              :search-input.sync="partSearch"
              item-text="part"
              item-value="part"
              :placeholder="$t('common.type_to_search')"
              cache-items
              hide-no-data
              solo
              :rules="[rules.required]"
              @input="setPartData"
            />
          </div>
        </b-col>

        <b-col
          cols="6"
          class="pb-0"
        >
          <div class="input-block input-block_white">
            <label class="input-block__label">
              {{ $t('views.shipment_notice_creation.quantity_sent') }}
            </label>
            <v-text-field
              v-model.number="item.quantitySent"
              type="number"
              solo
              :rules="[rules.positiveNumber]"
            />
          </div>
        </b-col>

        <b-col
          cols="6"
          class="pb-0"
        >
          <div class="input-block input-block_white">
            <label class="input-block__label">
              {{ $t('views.shipment_notice_creation.previous_total') }}
            </label>
            <v-text-field
              v-model="item.previousTotal"
              type="number"
              solo
            />
          </div>
        </b-col>

        <b-col
          cols="6"
          class="pb-0"
        >
          <div class="input-block input-block_white">
            <label class="input-block__label">
              {{ $t('common.uom') }}
            </label>
            <v-text-field
              v-model="item.UOM"
              readonly
              solo
            />
          </div>
        </b-col>

        <b-col
          cols="6"
          class="pb-0"
        >
          <div class="input-block input-block_white">
            <label class="input-block__label">
              {{ $t('views.shipment_notice_creation.weight_kg') }}
            </label>
            <v-text-field
              v-model="item.weightKg"
              type="number"
              solo
            />
          </div>
        </b-col>

        <b-col cols="6">
          <div class="input-block input-block_white">
            <label class="input-block__label">
              {{ $t('views.shipment_notice_creation.origin') }}
            </label>
            <v-autocomplete
              v-model="item.origin"
              :loading="loadingCountries"
              :items="countries"
              :search-input.sync="countrySearch"
              item-text="code"
              item-value="code"
              :placeholder="$t('common.type_to_search')"
              cache-items
              hide-no-data
              solo
              :rules="[rules.required]"
            />
          </div>
        </b-col>

        <b-col cols="6">
          <div class="input-block input-block_white">
            <label class="input-block__label">
              {{ $t('views.shipment_notice_creation.gtd') }}
            </label>
            <v-text-field
              v-model="item.GTD"
              solo
            />
          </div>
        </b-col>
      </b-row>

      <div class="v-application d-flex">
        <v-btn
          class="mr-4 flex-grow-1"
          outlined
          large
          @click="hideModal"
        >
          {{ $t('common.cancel') }}
        </v-btn>
        <v-btn
          color="primary"
          class="flex-grow-1"
          outlined
          large
          type="submit"
        >
          {{ isNew ? $t('common.add') : $t('common.save') }}
        </v-btn>
      </div>
    </v-form>
  </b-modal>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import { mongoRegexI } from '@/utils/mongo-regex';

const EMPTY_VALUE = {
  detailNumber: '',
  quantitySent: 0,
  previousTotal: 0,
  UOM: '',
  weightKg: 0,
  origin: '',
  GTD: '',
};

export default {
  name: 'PartModal',

  model: {
    prop: 'value',
  },

  props: {
    value: {
      type: Boolean,
      default: () => false,
    },

    selectedItem: {
      type: Object,
      default: () => null,
    },

    selectedIndex: {
      type: Number,
      default: () => -1,
    },

    isNew: {
      type: Boolean,
      required: true,
    },

    seller: {
      type: String,
      default: () => '',
    },

    buyer: {
      type: String,
      default: () => '',
    },

    shippingDate: {
      type: String,
    },

    manualContract: {
      type: Boolean,
      default: () => false,
    },

    contract: {
      type: Object,
      default: () => ({}),
    },
  },

  data() {
    return {
      item: { ...EMPTY_VALUE },
      rules: {
        required: (value) => Boolean(value) || this.$t('validation.required'),
        positiveNumber: (value) => {
          // eslint-disable-next-line no-restricted-globals
          if (!isNaN(value) && Number(value) > 0) { return true; }

          return this.$t('validation.positive_number');
        },
      },
      isShown: false,

      countries: [],
      loadingCountries: false,
      countrySearch: '',

      parts: [],
      loadingParts: false,
      partSearch: '',
    };
  },

  computed: {
    ...mapState(['user']),
    ...mapGetters(['business', 'editor', 'supplier']),

    title() {
      return this.isNew
        ? this.$t('views.shipment_notice_creation.add_part')
        : this.$t('views.shipment_notice_creation.edit_part');
    },
  },

  watch: {
    value(val) {
      this.isShown = val;
    },

    countrySearch(value) {
      if (value && value !== this.item.origin) {
        this.getCountries(value);
      }
    },

    partSearch(value) {
      if (!value || value === this.item.detailNumber) {
        return;
      }

      if (this.manualContract) {
        this.getPartsByContract(value);
      } else {
        this.getParts(value);
      }
    },
  },

  methods: {
    getItem() {
      this.item = { ...EMPTY_VALUE, ...(this.isNew ? {} : this.selectedItem) };
      if (!this.isNew) {
        this.parts.push(this.item.detailNumber);
        this.countries.push(this.item.origin);
      }
      this.$refs.form.resetValidation();
    },

    async getCountries(str) {
      this.loadingCountries = true;

      const query = {
        code: mongoRegexI(str),
      };

      try {
        const { data = {} } = await this.$http.get('/countries', {
          params: { query },
        });
        this.countries = data.rows;
      } catch (err) {
        this.$http.mockApiError(err);
      } finally {
        this.loadingCountries = false;
      }
    },

    // Only for ASN we will search parts by buyer
    async getParts(str) {
      this.loadingParts = true;

      // plant filter removed by #10005
      const query = {
        supplier: this.seller,
        // plant: this.buyer,
        part: mongoRegexI(str),
      };

      try {
        const { data = {} } = await this.$http.get('/current-sources-by-parts', {
          params: {
            query,
            date: this.$moment(this.shippingDate || undefined).startOf('day').toISOString(),
            onlyAvailable: true,
            limit: 10,
          },
        });
        this.parts = data.rows || [];
      } catch (err) {
        this.$http.mockApiError(err);
      } finally {
        this.loadingParts = false;
      }
    },

    async getPartsByContract(str) {
      this.loadingParts = true;

      const query = {
        supplier: this.seller,
        part: mongoRegexI(str),
      };

      try {
        const { data = {} } = await this.$http.get('/sources-by-parts', {
          params: {
            query,
            contract: this.contract.guid,
            onlyAvailable: true,
            limit: 10,
          },
        });
        this.parts = data.rows || [];
      } catch (err) {
        this.$http.mockApiError(err);
      } finally {
        this.loadingParts = false;
      }
    },

    async setPartData(partNumber) {
      if (!partNumber) {
        return;
      }

      try {
        const params = {
          pageSize: 1,
          query: { number: partNumber },
          fields: ['number', 'UOM', 'weight'],
        };

        // eslint-disable-next-line no-underscore-dangle
        const { data = {} } = await this.$http.get('/parts', { params });

        const part = (data.rows || []).shift() || {};

        this.item.detailNumber = partNumber;
        this.item.UOM = part.UOM;
        this.item.weightKg = part.weight;
      } catch (err) {
        this.$http.mockApiError(err);
      }
    },

    hideModal() {
      this.$emit('input', false);
    },

    submit() {
      if (!this.validate()) {
        return;
      }
      if (this.isNew) {
        this.$emit('addPart', this.item);
      } else {
        this.$emit('updatePart', this.selectedIndex, this.item);
      }
    },

    validate() {
      this.$refs.form.validate();
      const required = ['detailNumber', 'quantitySent', 'origin'];
      return required.every((key) => this.item[key]);
    },
  },
};
</script>
